// Services.js - Displays the main service gallery with interactive carousels.
// Manages hero slides, sub-carousels, and full-size image viewing.

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PortalModal from './PortalModal';
import {
    X,
    ChevronRight,
    ChevronLeft,
    Maximize2,
    Play,
    Pause,
    Sparkles,
    Image as ImageIcon
} from 'lucide-react';
import { fetchServices } from '../data/servicesData';
import ENV from '../config/env';

const FILE_SERVER_URL = ENV.FILE_SERVER_URL;
const API_BASE = ENV.API_BASE;

const getImagePath = (serviceFolder, fileName) => {
    return `/images/gallery/${serviceFolder}/${fileName}`;
};

const Services = () => {
    const [selectedService, setSelectedService] = useState(null);
    const [servicesData, setServicesData] = useState([]);
    const [serviceImages, setServiceImages] = useState({});
    const [mainCarouselIndex, setMainCarouselIndex] = useState(0);
    const [subCarouselIndices, setSubCarouselIndices] = useState({});
    const [isPlaying, setIsPlaying] = useState(true);
    const [showFullSizeImage, setShowFullSizeImage] = useState(false);
    const [selectedFullSizeIndex, setSelectedFullSizeIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const playIntervalRef = useRef(null);
    const [isMobile, setIsMobile] = useState(false);
    const horizontalCarouselRef = useRef(null);
    const [infiniteItems, setInfiniteItems] = useState([]);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [isHoveringCarousel, setIsHoveringCarousel] = useState(false);
    const [isHoveringHorizontal, setIsHoveringHorizontal] = useState(false);
    const autoPlayTimerRef = useRef(null);

    // Fetch services on mount
    useEffect(() => {
        fetchServicesData();
    }, []);

    const fetchServicesData = async () => {
        try {
            const services = await fetchServices();
            setServicesData(services);
            if (services.length > 0) {
                setSelectedService(services[0]);
            }
        } catch (error) {
            console.error('Error fetching services:', error);
        } finally {
            setLoading(false);
        }
    };

    // Handle modal overlay when full-size image is shown.
    useEffect(() => {
        if (!showFullSizeImage) return;
        document.body.classList.add("modal-open");
        document.body.style.overflow = "hidden";

        return () => {
            document.body.classList.remove("modal-open");
            document.body.style.overflow = "";
        };
    }, [showFullSizeImage]);

    // Creates an infinite array of services for seamless scrolling.
    useEffect(() => {
        if (servicesData.length > 0) {
            setInfiniteItems([...servicesData, ...servicesData, ...servicesData]);
        }
    }, [servicesData]);

    // Detects mobile device and updates state on resize.
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Loads all service images when services data changes.
    useEffect(() => {
        if (servicesData.length > 0) {
            loadAllServiceImages();
        }
        return () => {
            if (playIntervalRef.current) clearInterval(playIntervalRef.current);
            if (autoPlayTimerRef.current) clearTimeout(autoPlayTimerRef.current);
        };
    }, [servicesData]);

    /**
     * Auto-plays the main carousel with hover pause functionality
     */
    useEffect(() => {
        // Clear any existing interval
        if (playIntervalRef.current) {
            clearInterval(playIntervalRef.current);
            playIntervalRef.current = null;
        }

        // Don't start if not playing, transitioning, hovering, or no service selected
        if (!isPlaying || isTransitioning || isHoveringCarousel || !selectedService) {
            return;
        }

        // Start new interval
        playIntervalRef.current = setInterval(() => {
            const images = getServiceImages(selectedService);
            if (images.length > 0) {
                handleNextImage();
            }
        }, 1000);

        return () => {
            if (playIntervalRef.current) {
                clearInterval(playIntervalRef.current);
                playIntervalRef.current = null;
            }
        };
    }, [isPlaying, selectedService, serviceImages, isTransitioning, isHoveringCarousel]);

    /**
     * Auto-scroll horizontal carousel
     */
    useEffect(() => {
        // Clear any existing timer
        if (autoPlayTimerRef.current) {
            clearTimeout(autoPlayTimerRef.current);
            autoPlayTimerRef.current = null;
        }

        // Don't auto-scroll if hovering or no items
        if (isHoveringHorizontal || infiniteItems.length === 0 || !horizontalCarouselRef.current) {
            return;
        }

        // Auto-scroll every 2 seconds
        const scrollInterval = setInterval(() => {
            if (!isHoveringHorizontal && horizontalCarouselRef.current) {
                scrollHorizontalRight();
            }
        }, 1000);

        return () => {
            clearInterval(scrollInterval);
            if (autoPlayTimerRef.current) {
                clearTimeout(autoPlayTimerRef.current);
            }
        };
    }, [isHoveringHorizontal, infiniteItems.length]);

    /**
     * Loads all images for each service from the server.
     */
    const loadAllServiceImages = async () => {
        const loadedImages = {};
        const initialIndices = {};

        for (const service of servicesData) {
            try {
                const response = await fetch(`${FILE_SERVER_URL}/api/images/${service.galleryFolder}`);
                if (response.ok) {
                    const images = await response.json();
                    loadedImages[service.galleryFolder] = images.map(img => ({
                        ...img,
                        localPath: FILE_SERVER_URL + getImagePath(service.galleryFolder, img.name)
                    }));
                    initialIndices[service.galleryFolder] = 0;
                } else {
                    loadedImages[service.galleryFolder] = [];
                }
            } catch (error) {
                console.error(`Error loading images for ${service.galleryFolder}:`, error);
                loadedImages[service.galleryFolder] = [];
            }
        }

        setServiceImages(loadedImages);
        setSubCarouselIndices(initialIndices);
    };

    /**
     * Gets images for the selected service.
     */
    const getServiceImages = (service) => {
        return serviceImages[service?.galleryFolder] || [];
    };

    // Navigation functions
    const handleNextImage = () => {
        if (isTransitioning || !selectedService) return;
        const images = getServiceImages(selectedService);
        if (images.length === 0) return;

        setIsTransitioning(true);
        setMainCarouselIndex((prev) => (prev + 1) % images.length);

        setTimeout(() => { setIsTransitioning(false); }, 500);
    };

    const handlePrevImage = () => {
        if (isTransitioning || !selectedService) return;
        const images = getServiceImages(selectedService);
        if (images.length === 0) return;

        setIsTransitioning(true);
        setMainCarouselIndex((prev) => (prev - 1 + images.length) % images.length);

        setTimeout(() => { setIsTransitioning(false); }, 500);
    };

    const nextSubImage = (serviceId) => {
        const service = servicesData.find(s => s.id === serviceId);
        if (!service) return;
        const images = getServiceImages(service);
        if (images.length === 0) return;

        setSubCarouselIndices(prev => ({
            ...prev,
            [service.galleryFolder]: (prev[service.galleryFolder] + 1) % Math.min(images.length, 4)
        }));
    };

    // Periodically updates sub-carousel indices.
    useEffect(() => {
        const interval = setInterval(() => {
            setSubCarouselIndices(prev => {
                const newIndices = { ...prev };
                servicesData.forEach(service => {
                    const images = getServiceImages(service);
                    if (images.length > 0) {
                        newIndices[service.galleryFolder] =
                            (prev[service.galleryFolder] + 1) % Math.min(images.length, 4);
                    }
                });
                return newIndices;
            });
        }, 3000);

        return () => clearInterval(interval);
    }, [serviceImages, servicesData]);

    // Full-size image navigation
    const nextFullSizeImage = () => {
        if (!selectedService) return;
        const images = getServiceImages(selectedService);
        if (images.length === 0) return;
        setSelectedFullSizeIndex((prev) => (prev + 1) % images.length);
    };

    const prevFullSizeImage = () => {
        if (!selectedService) return;
        const images = getServiceImages(selectedService);
        if (images.length === 0) return;
        setSelectedFullSizeIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    // Open full-size image from hero slide button.
    const openFullSizeImageFromHero = () => {
        setSelectedFullSizeIndex(0);
        setShowFullSizeImage(true);
        setIsPlaying(false); // Pause carousel when viewing full size
    };

    // Close full-size image modal.
    const closeFullSizeImage = () => {
        setShowFullSizeImage(false);
        setIsPlaying(true); // Resume carousel when closing
    };

    // Infinite horizontal carousel navigation.
    const scrollHorizontalLeft = () => {
        if (horizontalCarouselRef.current) {
            const scrollAmount = isMobile ? 200 : 250;
            const newScrollLeft = horizontalCarouselRef.current.scrollLeft - scrollAmount;

            if (newScrollLeft <= 0) {
                horizontalCarouselRef.current.scrollLeft = horizontalCarouselRef.current.scrollWidth / 3;
            } else {
                horizontalCarouselRef.current.scrollLeft = newScrollLeft;
            }
        }
    };

    const scrollHorizontalRight = () => {
        if (horizontalCarouselRef.current) {
            const scrollAmount = isMobile ? 200 : 250;
            const newScrollLeft = horizontalCarouselRef.current.scrollLeft + scrollAmount;
            const maxScroll = horizontalCarouselRef.current.scrollWidth - horizontalCarouselRef.current.clientWidth;

            if (newScrollLeft >= maxScroll) {
                horizontalCarouselRef.current.scrollLeft = horizontalCarouselRef.current.scrollWidth / 3;
            } else {
                horizontalCarouselRef.current.scrollLeft = newScrollLeft;
            }
        }
    };

    // Handle scroll for infinite carousel.
    const handleHorizontalScroll = () => {
        if (!horizontalCarouselRef.current) return;

        const container = horizontalCarouselRef.current;
        const scrollWidth = container.scrollWidth;
        const scrollLeft = container.scrollLeft;

        if (scrollLeft >= (scrollWidth * 2 / 3)) {
            container.scrollLeft = scrollWidth / 3;
        } else if (scrollLeft <= 0) {
            container.scrollLeft = scrollWidth / 3;
        }
    };

    // Initialize carousel position.
    useEffect(() => {
        if (horizontalCarouselRef.current && infiniteItems.length > 0) {
            setTimeout(() => {
                horizontalCarouselRef.current.scrollLeft = horizontalCarouselRef.current.scrollWidth / 3;
            }, 100);
        }
    }, [infiniteItems.length]);

    // Handle keyboard navigation for full-size image.
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!showFullSizeImage) return;

            switch (e.key) {
                case 'Escape':
                    closeFullSizeImage();
                    break;
                case 'ArrowRight':
                    nextFullSizeImage();
                    break;
                case 'ArrowLeft':
                    prevFullSizeImage();
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showFullSizeImage, selectedFullSizeIndex, selectedService]);

    // Helper function to find the original service from infinite items.
    const getOriginalService = (service) => {
        return servicesData.find(s => s.id === service.id);
    };

    if (loading) {
        return (
            <section className="min-h-screen bg-white py-20 relative overflow-hidden flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Cargando servicios...</p>
                </div>
            </section>
        );
    }

    if (!selectedService || servicesData.length === 0) {
        return (
            <section id="servicios" className="min-h-screen bg-white py-20 relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 text-center">
                    <h2 className="text-4xl md:text-7xl font-bold gradient-text mb-4 md:mb-6 drop-shadow-md">
                        Nuestros Servicios
                    </h2>
                    <p className="text-lg md:text-xl text-gray-700">
                        Próximamente estaremos agregando nuestros servicios.
                    </p>
                </div>
            </section>
        );
    }

    return (
        <section id="servicios" className="min-h-screen bg-white py-12 md:py-20 relative overflow-hidden">
            {/* Stars */}
            <div className="absolute inset-0">
                {[...Array(50)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute w-1 h-1 bg-secondary rounded-full"
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                        }}
                        animate={{ opacity: [0.2, 1, 0.2], scale: [1, 1.5, 1] }}
                        transition={{
                            duration: Math.random() * 3 + 2,
                            repeat: Infinity,
                            delay: Math.random() * 2,
                        }}
                    />
                ))}
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-8 md:mb-12"
                >
                    <h2 className="text-4xl md:text-7xl font-bold gradient-text mb-4 md:mb-6 drop-shadow-md">
                        Nuestros Servicios
                    </h2>
                    <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                        ¡Haz que tu imagen destaque con nosotros!
                    </p>
                </motion.div>

                {/* Main Carousel Section */}
                <div className="mb-8 md:mb-12">
                    <div className="relative rounded-3xl overflow-hidden border border-gray-200 bg-white p-1 shadow-lg">
                        {/* Main Carousel Container */}
                        <div
                            className="relative h-[400px] md:h-[600px] rounded-2xl overflow-hidden"
                            onMouseEnter={() => setIsHoveringCarousel(true)}
                            onMouseLeave={() => setIsHoveringCarousel(false)}
                        >
                            <AnimatePresence mode="wait">
                                {getServiceImages(selectedService).length > 0 && (
                                    <motion.div
                                        key={mainCarouselIndex}
                                        initial={{ opacity: 0, scale: 1.1 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.9 }}
                                        transition={{ duration: 0.5 }}
                                        className="absolute inset-0"
                                    >
                                        <img
                                            src={getServiceImages(selectedService)[mainCarouselIndex]?.localPath}
                                            alt={`${selectedService.title} ${mainCarouselIndex + 1}`}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = `${FILE_SERVER_URL}/images/gallery/default.jpg`;
                                            }}
                                        />
                                        {/* Gradient overlay */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent md:from-black/80 md:via-black/40"></div>
                                        <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 mix-blend-overlay"></div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Service info overlay */}
                            <div className="absolute bottom-0 left-0 right-0 p-4 md:p-8 bg-gradient-to-t from-black/90 to-transparent">
                                <div className="flex flex-col md:flex-row md:items-center justify-between">
                                    <div className="mb-4 md:mb-0">
                                        <div className="flex items-center space-x-2 md:space-x-4 mb-2 md:mb-3">
                                            {selectedService.icon && (
                                                <span className="text-2xl md:text-4xl">{selectedService.icon}</span>
                                            )}
                                            <h3 className="text-2xl md:text-5xl font-bold text-white">
                                                {selectedService.title}
                                            </h3>
                                        </div>
                                        <p className="text-gray-300 text-sm md:text-lg max-w-2xl line-clamp-2 md:line-clamp-none">
                                            {selectedService.description}
                                        </p>
                                    </div>
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={openFullSizeImageFromHero}
                                        className="bg-gradient-to-r from-primary to-secondary text-white px-4 py-3 md:px-8 md:py-4 rounded-full flex items-center justify-center space-x-2 md:space-x-3 text-sm md:text-lg font-semibold glass-effect hover-glow transition-shadow w-full md:w-auto"
                                    >
                                        <Maximize2 size={isMobile ? 20 : 24} />
                                        <span>{isMobile ? 'Ver Todo' : 'Ver Galería Completa'}</span>
                                    </motion.button>
                                </div>
                            </div>

                            {/* Image counter */}
                            {getServiceImages(selectedService).length > 0 && (
                                <div className="absolute top-4 right-4 md:top-8 md:right-8 bg-black/50 rounded-full px-3 py-1 md:px-4 md:py-2 glass-effect hover-glow">
                                    <span className="text-white font-mono text-sm md:text-base">
                                        {mainCarouselIndex + 1} / {getServiceImages(selectedService).length}
                                    </span>
                                </div>
                            )}

                            {/* Navigation controls */}
                            <div className="absolute left-2 md:left-8 top-1/2 transform -translate-y-1/2">
                                <motion.button
                                    whileHover={{ scale: 1.1, x: -5 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={handlePrevImage}
                                    disabled={isTransitioning}
                                    className={`bg-black/50 border border-white/20 p-2 md:p-4 rounded-full text-white transition-colors glass-effect hover-glow hover:bg-primary hover:scale-110`}
                                >
                                    <ChevronLeft size={isMobile ? 24 : 32} />
                                </motion.button>
                            </div>
                            <div className="absolute right-2 md:right-8 top-1/2 transform -translate-y-1/2">
                                <motion.button
                                    whileHover={{ scale: 1.1, x: 5 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={handleNextImage}
                                    disabled={isTransitioning}
                                    className={`bg-black/50 border border-white/20 p-2 md:p-4 rounded-full text-white transition-colors glass-effect hover-glow hover:bg-primary hover:scale-110`}
                                >
                                    <ChevronRight size={isMobile ? 24 : 32} />
                                </motion.button>
                            </div>

                            {/* Play/Pause button */}
                            <div className="absolute top-4 left-4 md:top-8 md:left-8">
                                <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => setIsPlaying(!isPlaying)}
                                    className="bg-black/50 border border-white/20 p-2 md:p-3 rounded-full text-white glass-effect hover-glow transition-colors"
                                >
                                    {isPlaying ? <Pause size={isMobile ? 20 : 24} /> : <Play size={isMobile ? 20 : 24} />}
                                </motion.button>
                            </div>
                        </div>

                        {/* Dots indicator */}
                        {getServiceImages(selectedService).length > 1 && (
                            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-1 md:space-x-2">
                                {getServiceImages(selectedService).map((_, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => {
                                            if (!isTransitioning) {
                                                setIsTransitioning(true);
                                                setMainCarouselIndex(idx);
                                                setTimeout(() => setIsTransitioning(false), 500);
                                            }
                                        }}
                                        disabled={isTransitioning}
                                        className={`rounded-full transition-all duration-300 ${idx === mainCarouselIndex
                                            ? 'bg-white w-6 md:w-8 h-2 md:h-3'
                                            : 'bg-white/40 hover:bg-white/60 w-2 h-2 md:w-3 md:h-3'
                                            } ${isTransitioning ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Horizontal Services Carousel */}
                <div className="mb-8 md:mb-12 relative">
                    {/* Carousel Header */}
                    <div className="flex items-center justify-between mb-4 md:mb-6">
                        <h3 className="text-xl md:text-3xl font-bold text-gray-900">Selecciona un Servicio</h3>
                        <div className="flex space-x-2">
                            <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => {
                                    scrollHorizontalLeft();
                                    // Pause auto-scroll briefly after manual interaction
                                    setIsHoveringHorizontal(true);
                                    setTimeout(() => setIsHoveringHorizontal(false), 5000);
                                }}
                                className="bg-white border border-gray-300 p-2 md:p-3 rounded-full text-gray-700 hover:bg-primary hover:text-white transition-all glass-effect hover-glow"
                            >
                                <ChevronLeft size={isMobile ? 20 : 24} />
                            </motion.button>
                            <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => {
                                    scrollHorizontalRight();
                                    // Pause auto-scroll briefly after manual interaction
                                    setIsHoveringHorizontal(true);
                                    setTimeout(() => setIsHoveringHorizontal(false), 5000);
                                }}
                                className="bg-white border border-gray-300 p-2 md:p-3 rounded-full text-gray-700 hover:bg-secondary hover:text-white transition-all glass-effect hover-glow"
                            >
                                <ChevronRight size={isMobile ? 20 : 24} />
                            </motion.button>
                        </div>
                    </div>

                    {/* Horizontal Carousel Container */}
                    <div className="relative">
                        {/* Left gradient fade */}
                        <div className="absolute left-0 top-0 bottom-0 w-12 md:w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"></div>
                        {/* Right gradient fade */}
                        <div className="absolute right-0 top-0 bottom-0 w-12 md:w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>

                        {/* Infinite Carousel Items */}
                        <div
                            ref={horizontalCarouselRef}
                            className="flex space-x-3 md:space-x-4 overflow-x-auto scrollbar-hide pb-4 scroll-smooth"
                            onScroll={handleHorizontalScroll}
                            onMouseEnter={() => setIsHoveringHorizontal(true)}
                            onMouseLeave={() => setIsHoveringHorizontal(false)}
                            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                        >
                            {infiniteItems.map((service, index) => {
                                const images = getServiceImages(service);
                                const currentIndex = subCarouselIndices[service.galleryFolder] || 0;
                                const isSelected = selectedService?.id === service.id;

                                return (
                                    <motion.div
                                        key={`${service.id}-${index}`}
                                        whileHover={{ scale: 1.05 }}
                                        className={`flex-shrink-0 w-40 md:w-48 rounded-xl overflow-hidden cursor-pointer bg-white border ${isSelected
                                            ? 'border-2 border-primary shadow-lg shadow-primary/20'
                                            : 'border-gray-200 hover:border-primary/50'
                                            }`}
                                        onClick={() => {
                                            const originalService = getOriginalService(service);
                                            if (originalService) {
                                                setSelectedService(originalService);
                                                setMainCarouselIndex(0);
                                            }
                                        }}
                                    >
                                        {/* Service image carousel */}
                                        <div className="relative h-28 md:h-32 overflow-hidden">
                                            {images.length > 0 ? (
                                                <AnimatePresence mode="wait">
                                                    <motion.div
                                                        key={currentIndex}
                                                        initial={{ opacity: 0, x: 10 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        exit={{ opacity: 0, x: -10 }}
                                                        transition={{ duration: 0.3 }}
                                                        className="absolute inset-0"
                                                    >
                                                        <img
                                                            src={images[currentIndex]?.localPath}
                                                            alt={`${service.title} preview`}
                                                            className="w-full h-full object-cover"
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.src = `${FILE_SERVER_URL}/images/gallery/default.jpg`;
                                                            }}
                                                        />
                                                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                                                    </motion.div>
                                                </AnimatePresence>
                                            ) : (
                                                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center">
                                                    <ImageIcon className="text-gray-400" size={isMobile ? 24 : 28} />
                                                </div>
                                            )}
                                        </div>

                                        {/* Service info - Only title */}
                                        <div className="p-3">
                                            <div className="flex items-center justify-center">
                                                <h4 className="text-sm md:text-base font-semibold text-gray-900 text-center truncate w-full">
                                                    {service.title}
                                                </h4>
                                            </div>
                                        </div>

                                        {/* Selected indicator - Always visible for selected service */}
                                        {isSelected && (
                                            <div className="absolute top-2 right-2">
                                                <motion.div
                                                    animate={{ scale: [1, 1.2, 1] }}
                                                    transition={{ duration: 2, repeat: Infinity }}
                                                    className="w-2.5 h-2.5 bg-primary rounded-full shadow-lg"
                                                />
                                            </div>
                                        )}
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {/* Full Size Image Modal */}
            <AnimatePresence>
                {showFullSizeImage && selectedService && getServiceImages(selectedService).length > 0 && (
                    <PortalModal isOpen={showFullSizeImage}>
                        <AnimatePresence>
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="fixed inset-0 bg-black/95 z-[20002] flex items-center justify-center p-2 md:p-4"
                                onClick={closeFullSizeImage}
                            >
                                {/* Close button */}
                                <motion.button
                                    initial={{ opacity: 0, y: -20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    onClick={closeFullSizeImage}
                                    className="absolute top-4 right-4 md:top-8 md:right-8 z-10 bg-black/50 border border-white/20 p-3 rounded-full text-white hover:bg-white/20 transition-colors"
                                >
                                    <X size={isMobile ? 24 : 32} />
                                </motion.button>

                                {/* Navigation buttons */}
                                {getServiceImages(selectedService).length > 1 && (
                                    <>
                                        <motion.button
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -20 }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                prevFullSizeImage();
                                            }}
                                            className="absolute left-2 md:left-8 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 border border-white/20 p-3 md:p-4 rounded-full text-white glass-effect hover-glow hover:bg-primary hover:scale-110"
                                        >
                                            <ChevronLeft size={isMobile ? 24 : 32} />
                                        </motion.button>

                                        <motion.button
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: 20 }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                nextFullSizeImage();
                                            }}
                                            className="absolute right-2 md:right-8 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 border border-white/20 p-3 md:p-4 rounded-full text-white glass-effect hover-glow hover:bg-primary hover:scale-110"
                                        >
                                            <ChevronRight size={isMobile ? 24 : 32} />
                                        </motion.button>
                                    </>
                                )}

                                {/* Image counter */}
                                <div className="absolute top-4 left-4 md:top-8 md:left-8 bg-black/50 text-white px-3 py-1 rounded-full text-sm z-10">
                                    {selectedFullSizeIndex + 1} / {getServiceImages(selectedService).length}
                                </div>

                                {/* Image container */}
                                <motion.div
                                    initial={{ scale: 0.9, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0.9, opacity: 0 }}
                                    className="relative w-full max-w-6xl max-h-[90vh]"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {/* Image */}
                                    <motion.div
                                        key={selectedFullSizeIndex}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.3 }}
                                        className="relative flex items-center justify-center"
                                    >
                                        <img
                                            src={getServiceImages(selectedService)[selectedFullSizeIndex]?.localPath}
                                            alt={`${selectedService.title} ${selectedFullSizeIndex + 1}`}
                                            className="max-w-full max-h-[80vh] w-auto h-auto object-contain rounded-lg"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = `${FILE_SERVER_URL}/images/gallery/default.jpg`;
                                            }}
                                        />

                                        {/* Simplified image info */}
                                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-4 md:p-6 rounded-b-lg">
                                            <div>
                                                <div className="flex items-center space-x-2 md:space-x-4 mb-1">
                                                    {selectedService.icon && (
                                                        <span className="text-xl md:text-2xl">{selectedService.icon}</span>
                                                    )}
                                                    <h3 className="text-lg md:text-2xl font-bold text-white">
                                                        {selectedService.title}
                                                    </h3>
                                                </div>
                                                <p className="text-gray-300 text-sm md:text-base mb-2 line-clamp-2">
                                                    {selectedService.description}
                                                </p>
                                            </div>
                                        </div>
                                    </motion.div>

                                    {/* Thumbnail strip */}
                                    {getServiceImages(selectedService).length > 1 && (
                                        <div className="mt-4 flex items-center justify-center space-x-2 overflow-x-auto py-2 px-4">
                                            {getServiceImages(selectedService).map((img, idx) => (
                                                <button
                                                    key={idx}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedFullSizeIndex(idx);
                                                    }}
                                                    className={`flex-shrink-0 w-12 h-12 md:w-16 md:h-16 rounded-lg overflow-hidden border-2 transition-all duration-300 ${idx === selectedFullSizeIndex
                                                        ? 'border-primary scale-110'
                                                        : 'border-transparent opacity-60 hover:opacity-100'
                                                        }`}
                                                >
                                                    <img
                                                        src={img.localPath}
                                                        alt={`Thumbnail ${idx + 1}`}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = `${FILE_SERVER_URL}/images/gallery/default.jpg`;
                                                        }}
                                                    />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </motion.div>

                                {/* Keyboard hint for mobile */}
                                {isMobile && (
                                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-center">
                                        <p className="text-gray-400 text-sm">Desliza para navegar • Toca para cerrar</p>
                                    </div>
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </PortalModal>
                )}
            </AnimatePresence>

            {/* Add custom scrollbar hide styles */}
            <style>{`
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`}</style>
        </section>
    );
};

export default Services;